import CrusadeCard from "@/model/CrusadeCard";
import { BattleFieldRole, Rank, UnitType } from "@/model/Enums";
import { loadAsync } from "jszip";
import xml2js from "xml2js";
import { parseBooleans, parseNumbers } from "xml2js/lib/processors";

type BattleScribeCosts = {
  $: { name: string; value: number };
};

type BattleScribeCategory = {
  $: { primary: boolean; name: string };
};

type BattleScribeSelection = {
  $: { name: string; type: string; number: number };
  costs: { cost: BattleScribeCosts[] }[];
  categories: { category: BattleScribeCategory[] }[];
};

type BattleScribeForce = {
  $: {};
  selections: { selection: BattleScribeSelection[] }[];
};

type BattleScribeRoster = {
  roster: { $: { name: string }; forces: { force: BattleScribeForce[] }[] };
};

function trimString(val: string): string {
  return val?.trim() || "";
}

export default class BattlescribeImportService {
  public async extractZip(file: ArrayBuffer): Promise<string> {
    const files = (await loadAsync(file)).files;
    const firstKey = Object.keys(files)[0];
    return await files[firstKey].async("string");
  }

  public async extractXmlObject(
    file: ArrayBuffer
  ): Promise<BattleScribeRoster> {
    const extractedFile = await this.extractZip(file);
    return await xml2js.parseStringPromise(extractedFile, {
      attrValueProcessors: [trimString, parseNumbers, parseBooleans],
    });
  }

  public async extractCrusadeCards(file: ArrayBuffer): Promise<CrusadeCard[]> {
    const extractedRoster = await this.extractXmlObject(file);
    const result: CrusadeCard[] = [];
    for (const forces of extractedRoster.roster.forces) {
      for (const force of forces.force) {
        if (!force.selections) {
          continue;
        }
        for (const selections of force.selections) {
          for (const selection of selections.selection) {
            if (!["model", "unit"].includes(selection.$.type)) {
              continue;
            }
            const primaryCategory =
              selection.categories[0]?.category.find((c) => c.$.primary)?.$
                .name || null;
            if (primaryCategory === "Configuration") {
              continue;
            }
            const role =
              Object.entries(BattleFieldRole).find(
                ([key]) => key === primaryCategory
              )?.[1] || undefined;
            const powerRating =
              selection.costs[0]?.cost.find((c) => c.$.name === "PL")?.$
                .value || 0;
            const unitTypeValues: string[] = selection.categories[0]?.category
              .filter((c) => Object.keys(UnitType).includes(c.$.name))
              ?.map((v) => v.$.name);
            if (unitTypeValues.length < 1) {
              unitTypeValues.push("Other");
            }
            const unitTypes: UnitType[] = Object.entries(UnitType)
              .filter(([key]) => unitTypeValues.includes(key))
              ?.map((arr) => arr[1]);
            // ?.map((arr) => arr[1]);
            result.push({
              name: selection.$.name,
              powerRating: powerRating,
              crusadePoints: 0,
              battleFieldRole: role,
              rank: Rank["Battle Ready"],
              experience: 0,
              markedForGreatnessCount: 0,
              unitType: unitTypes,
              combatTallies: [],
              battleHonours: "",
              battleScars: "",
              battleTally: 0,
              battlesSurvived: 0,
              equipment: "",
              notes: "",
              otherUpgrades: "",
              psychicPowers: [],
              relics: [],
              warlordTraits: [],
              keywords:
                selection.categories[0]?.category.map((c) => c.$.name) || [],
            });
          }
        }
      }
    }
    return result;
  }
}
